import keys from '@debbie/common/dist/cache/keys.js';
import { PaymentPlanEntryStatus, PaymentPlanStatus, } from '@debbie/common/dist/economy/payment-plans/index.js';
import { PaymentMethodStatus } from '@debbie/common/dist/payment-methods/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as paymentPlansAPI from '@debbie/cortex/dist/api/payment-plans.js';
import { paymentPlansEntity } from '@debbie/cortex/dist/paymentPlans/index.js';
import q from '@debbie/cortex/dist/query.js';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../../theme.js';
import Badge from '../../Badge.js';
import Button from '../../Button.js';
import CopyToClipboard from '../../CopyToClipboard.js';
import ExclusiveContent from '../../ExclusiveContent.js';
import { ArrowDownCircle } from '../../Icons/index.js';
import Money from '../../Money.js';
import PerformedBy from '../../performed-by/PerformedBy.js';
import Table from '../../Table.js';
import Logs from '../PaymentPlanLogs.js';
const PaymentPlanEntries = (props) => {
    const { paymentPlan } = props;
    const lang = useSelector((s) => s.lang);
    const [collapsed, setCollapsed] = useState(![PaymentPlanStatus.ACTIVE, PaymentPlanStatus.CREATED].includes(paymentPlan.status));
    const handle = (entry) => `${entry.amount}${entry.status}${entry.type}`;
    const entryGroups = paymentPlan.plan.reduce((acc, entry) => {
        // Initial create group
        if (!acc[acc.length - 1]) {
            acc = [[entry]];
            return acc;
        }
        const lastGroup = acc[acc.length - 1];
        const lastEntry = lastGroup[lastGroup.length - 1];
        const entryHandle = handle(entry);
        const lastEntryHandle = handle(lastEntry);
        // Add to group
        if (entryHandle === lastEntryHandle) {
            acc[acc.length - 1] = [...lastGroup, entry];
            return acc;
        }
        // Create new group
        else {
            return [...acc, [entry]];
        }
    }, []);
    return (React.createElement(PaymentPlanStyled, null,
        React.createElement("div", { style: {
                cursor: 'pointer',
            }, onClick: () => setCollapsed(!collapsed) },
            React.createElement("div", { style: { float: 'right' } },
                paymentPlan.deletedBy && (React.createElement(PerformedBy, { performedBy: paymentPlan.deletedBy })),
                paymentPlan.deletedAt && (React.createElement("span", null, moment(paymentPlan.deletedAt).format(THEME.DATE_FORMAT_LONG))),
                ' ',
                React.createElement(Badge, { theme: paymentPlan.status === PaymentPlanStatus.ACTIVE
                        ? THEME.SUCCESS
                        : 'grey' }, lang.paymentPlans[paymentPlan.status] || paymentPlan.status)),
            React.createElement("h3", null,
                paymentPlan.createdBy && (React.createElement(PerformedBy, { performedBy: paymentPlan.createdBy })),
                ' ',
                lang.paymentPlans.paymentPlan,
                ' ',
                moment(paymentPlan.createdAt).format(THEME.DATE_FORMAT_LONG))),
        !collapsed && (React.createElement(React.Fragment, null,
            React.createElement("div", null, props.children),
            React.createElement(Table, { zebra: true },
                React.createElement("tr", null,
                    React.createElement("th", { colSpan: 2 },
                        lang.paymentPlans.entry,
                        " "),
                    React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.reminder),
                    React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.date),
                    React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.lastChanceOfPayment),
                    React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.cancelled),
                    React.createElement("th", { style: { textAlign: 'right' } }, lang.paymentPlans.amount)),
                entryGroups.map((entryGroup, i) => (React.createElement(EntryGroup, { key: i + JSON.stringify(entryGroup), entryGroup: entryGroup, pastEntriesCount: entryGroups
                        .slice(0, i)
                        .reduce((acc, eG) => eG.length + acc, 0), paymentPlanId: paymentPlan.paymentPlanId, paymentPlan: paymentPlan }))))))));
};
const EntryGroup = (props) => {
    const { entryGroup, pastEntriesCount } = props;
    const lang = useSelector((s) => s.lang);
    const [collapsed, setCollapsed] = useState(true);
    if (entryGroup.length > 4 && collapsed) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Entry, { entry: entryGroup[0], num: pastEntriesCount + 1, paymentPlanId: props.paymentPlanId, paymentPlanEntryId: entryGroup[0].paymentPlanEntryId, paymentPlan: props.paymentPlan }),
            React.createElement("tr", { style: { cursor: 'pointer' } },
                React.createElement("td", { colSpan: 7 },
                    React.createElement("p", { style: {
                            fontSize: '.8em',
                            fontWeight: 'bold',
                            margin: '0.4em 0',
                        }, onClick: () => setCollapsed(!collapsed) },
                        React.createElement(ArrowDownCircle, { style: {
                                margin: '0.15em 6px -.15em 0',
                                transform: 'rotate(-90deg)',
                            } }),
                        "...",
                        entryGroup.length - 2,
                        " ",
                        lang.paymentPlans.collapsedEntries))),
            React.createElement(Entry, { entry: entryGroup[entryGroup.length - 1], num: pastEntriesCount + entryGroup.length, paymentPlanId: props.paymentPlanId, paymentPlanEntryId: entryGroup[entryGroup.length - 1].paymentPlanEntryId, paymentPlan: props.paymentPlan })));
    }
    return (React.createElement(React.Fragment, null, entryGroup.map((entry, i) => {
        return (React.createElement(Entry, { key: `${entry.amount}${entry.status}${entry.type}${i}`, entry: entry, num: pastEntriesCount + i + 1, paymentPlanId: props.paymentPlanId, paymentPlanEntryId: entry.paymentPlanEntryId, paymentPlan: props.paymentPlan }));
    })));
};
const Entry = (props) => {
    const lang = useSelector((s) => s.lang);
    const settle = useMutation(async () => {
        if (!props.paymentPlanEntryId) {
            throw new Error('No payment plan entry id');
        }
        await paymentPlansAPI.settle(props.paymentPlanEntryId);
        // Reload payment plans
        q.invalidate(keys.write.create(paymentPlansEntity));
    }, {
        onError: (e) => {
            console.error('Error settling payment plan entry', e);
            alert(e.message ? e.message : 'Error');
        },
    });
    const { entry, num } = props;
    return (React.createElement("tr", null,
        React.createElement("td", { style: { width: '40px' } },
            React.createElement(Badge, { theme: THEME.COLOR_ACCENT },
                React.createElement(CopyToClipboard, { text: props.paymentPlanEntryId || '', hideIcon: true }, num)),
            ' '),
        React.createElement("td", null,
            React.createElement(Badge, { theme: entry.status === PaymentPlanEntryStatus.SETTLED
                    ? THEME.SUCCESS
                    : entry.status === PaymentPlanEntryStatus.CANCELED
                        ? THEME.DANGEROUS
                        : entry.status !== PaymentPlanEntryStatus.PENDING
                            ? THEME.COLOR_ACCENT
                            : 'grey' }, lang.paymentPlanEntryStatuses[entry.status]),
            ' ',
            props.paymentPlanId &&
                (props.paymentPlanEntryId === null || !!props.paymentPlanEntryId) && (React.createElement(Logs, { paymentPlanId: props.paymentPlanId, paymentPlanEntryId: props.paymentPlanEntryId })),
            React.createElement(ExclusiveContent, null, props.entry.status === PaymentPlanEntryStatus.PENDING &&
                props.paymentPlan?.paymentMethodStatus ===
                    PaymentMethodStatus.SAVED && (React.createElement(Button, { size: "mini", theme: "dangerous", loading: settle.isLoading, onClick: () => settle.mutate() }, "Settle now")))),
        React.createElement("td", { style: { textAlign: 'right' } }, moment(entry.reminder).format(THEME.DATE_FORMAT_SHORT)),
        React.createElement("td", { style: { textAlign: 'right' } },
            React.createElement("b", null, moment(entry.date).format(THEME.DATE_FORMAT_SHORT))),
        React.createElement("td", { style: { textAlign: 'right' } }, moment(entry.deadline).format(THEME.DATE_FORMAT_SHORT)),
        React.createElement("td", { style: { textAlign: 'right' } }, moment(entry.cancel).format(THEME.DATE_FORMAT_SHORT)),
        React.createElement("td", { style: { textAlign: 'right' } },
            React.createElement("b", null,
                React.createElement(Money, { amount: entry.amount || 0 })))));
};
const PaymentPlanStyled = styled.div `
  padding: 20px 0;
  margin-bottom: 0px;

  border-bottom: 1px solid grey;
  &:first-child {
    border-top: 1px solid grey;
  }
`;
export default PaymentPlanEntries;
//# sourceMappingURL=PaymentPlanEntries.js.map